import { useEffect, useState } from "react";

import { GetRoleDetailsResponse } from "@/Administration/Api/Roles/Dto/GetRoleDetailsRequestResponse";
import { RolesApi } from "@/Administration/Api/Roles/RolesApi";
import { RoleInfoComponent } from "@/Administration/Components/Roles/RoleInfoComponent";
import { RolesSearchVisibilityToggle } from "@/Administration/Components/Roles/RolesSearchVisibilityToggle";
import { SearchableRolesList } from "@/Administration/Components/Roles/SearchableRolesList";
import { RoleHeaderModel } from "@/Administration/Models/Role/RoleHeaderModel";
import { RoleModel } from "@/Administration/Models/Role/RoleModel";
import { GenericResponse } from "@/Common/Api/Dto/GenericResponse";
import { FormMode } from "@/Common/Models/UI/Enums";

import styles from "./RoleManagementComponent.module.scss";

function RoleManagementComponent(): JSX.Element {
    const [showSearch, setShowSearch] = useState(true);
    const [roleHeaders, setRoleHeaders] = useState<RoleHeaderModel[]>([]);
    const [selectedRole, setSelectedRole] = useState<RoleModel>(null);
    const [formMode, setFormMode] = useState<FormMode>(FormMode.View);

    useEffect(() => {
        $(window).trigger("resize");
        refreshRoleHeaders();
    }, []);

    const refreshRoleHeaders = async (): Promise<void> => {
        const response = await RolesApi.GetCachedRoleHeadersAsync();

        if (!response?.IsSuccess) {
            sessionStore.showPopup(response.Errors);
            return;
        }

        setRoleHeaders(response.RoleHeaders);
    };

    const saveRole = async (role: RoleModel): Promise<void> => {
        if (role === null)
            return;

        if (formMode == FormMode.New)
            await createRole(role);
        else
            await updateRole(role);
    };

    const createRole = async (role: RoleModel): Promise<void> => {
        const response = await RolesApi.CreateRole(
            {
                name: role.Name,
                description: role.Description,
                userIds: role.UserIds,
                permissionIds: role.PermissionIds
            }
        );

        if (!response?.IsSuccess) {
            sessionStore.showPopup(response.Errors);
            return;
        }

        setFormMode(FormMode.View);
        await refreshRoleHeaders();
        setSelectedRole({ ...role, Id: response.RoleId });
    };

    const updateRole = async (nextRole: RoleModel): Promise<void> => {
        const currentRole = selectedRole;

        const userIdsToRemove = currentRole.UserIds.filter(item => !nextRole.UserIds.contains(item));
        const userIdsToAdd = nextRole.UserIds.filter(item => !currentRole.UserIds.contains(item));

        const permissionIdsToRemove = currentRole.PermissionIds.filter(item => !nextRole.PermissionIds.contains(item));
        const permissionIdsToAdd = nextRole.PermissionIds.filter(item => !currentRole.PermissionIds.contains(item));

        const response = await RolesApi.UpdateRole(
            {
                roleId: nextRole.Id,
                name: nextRole.Name,
                description: nextRole.Description,
                userIdsToAdd: userIdsToAdd,
                userIdsToRemove: userIdsToRemove,
                permissionIdsToAdd: permissionIdsToAdd,
                permissionIdsToRemove: permissionIdsToRemove
            }
        );

        if (!response?.IsSuccess) {
            sessionStore.showPopup(response.Errors);
            return;
        }

        setFormMode(FormMode.View);
        await refreshRoleHeaders();
        setSelectedRole({ ...nextRole });
    };

    const deleteRole = async (deleteAllUsers: boolean): Promise<void> => {
        if (selectedRole?.Id) {
            const response: GenericResponse = await RolesApi.DeleteRole(
                selectedRole.Id,
                deleteAllUsers ? selectedRole.UserIds : []
            );

            if (!response?.IsSuccess && response?.Errors?.length > 0) {
                sessionStore.showPopup(response.Errors);
                return;
            }

            setSelectedRole(null);
            setFormMode(FormMode.View);
            await refreshRoleHeaders();
        }
    };

    const selectRole = (roleId: string): void => {
        const loadRoleDetails = async (roleId: string): Promise<void> => {
            const response: GetRoleDetailsResponse = await RolesApi.GetRoleDetails({ roleId });

            if (!response.Errors.any()) {
                setSelectedRole({ ...response.Role, Description: response.Role.Description ?? "" });
                setFormMode(FormMode.View);
            }
        };
        if (roleId) {
            loadRoleDetails(roleId);
        }
    };

    const toggleSearchPanel = (): void => {
        setShowSearch(!showSearch);
        if (!showSearch) {
            $(window).trigger("resize");
        }
    };

    return (
        <div className="fxheight">
            <div className={styles.adminRolesContainer}>
                {showSearch && (
                    <SearchableRolesList
                        roles={roleHeaders}
                        selectedRoleId={selectedRole?.Id}
                        onRoleClick={selectRole}
                        onCreateButtonClick={() => setFormMode(FormMode.New)}
                        hideSelectionMarker={formMode === FormMode.New}
                    />
                )}
                <RolesSearchVisibilityToggle onClick={() => toggleSearchPanel()} />
                <RoleInfoComponent
                    role={selectedRole}
                    formMode={formMode}
                    onSaveButtonClick={role => saveRole(role)}
                    onDeleteButtonClick={(_role: RoleModel, deleteAllUsers: boolean) => deleteRole(deleteAllUsers)}
                    onCancelButtonClick={() => setFormMode(FormMode.View)}
                    onModifyButtonClick={() => setFormMode(FormMode.Edit)}
                />
            </div>
        </div>
    );
}

export { RoleManagementComponent };
